import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { typeById } from '../demo/catalog.js';
import { useDemo } from '../demo/DemoContext.jsx';
import { loadTickets, titleFor } from '../demo/store.js';
import { NewReplyChip, Pri, ServerNotice, SourceChip, SourceLegend, StatusChip } from '../components/Chips.jsx';
import { useUnread } from '../lib/useUnread.js';
import { isOpen } from '../lib/ticketView.js';
import { shortWhen } from '../lib/tone.js';

export default function MyTickets() {
  const { me, version } = useDemo();
  const [tickets, setTickets] = useState(null);
  const [serverState, setServerState] = useState('ok');
  const [hiddenDemo, setHiddenDemo] = useState([]);
  const [filter, setFilter] = useState('open');
  const unread = useUnread();

  useEffect(() => {
    let cancelled = false;
    loadTickets({ mine: true, email: me.email }).then((r) => {
      if (cancelled) return;
      setTickets(r.rows);
      setServerState(r.serverState);
      setHiddenDemo(r.hiddenDemo);
    });
    return () => {
      cancelled = true;
    };
  }, [me.email, version]);

  const all = tickets || [];
  const open = all.filter(isOpen);
  const done = all.filter((t) => !isOpen(t));
  const shown = filter === 'open' ? open : done;

  return (
    <>
      <section className="hero" style={{ paddingBottom: 0 }}>
        <h1 className="hero__title" style={{ fontSize: 34 }}>
          My <strong>tickets</strong>
        </h1>
        <p className="hero__sub">
          Everything you've raised, and where each one has got to. The conversation lives on the
          ticket — there is nothing to dig out of an inbox.
        </p>
      </section>

      <ServerNotice state={serverState} />
      <SourceLegend rows={tickets} hiddenDemo={hiddenDemo} />

      <div className="qtools" style={{ marginTop: 16, borderRadius: 'var(--ihs-r-page)', border: '1px solid var(--ihs-border-card)' }}>
        <button type="button" className={`qtab${filter === 'open' ? ' qtab--on' : ''}`} onClick={() => setFilter('open')}>
          Open<b>{open.length}</b>
        </button>
        <button type="button" className={`qtab${filter === 'done' ? ' qtab--on' : ''}`} onClick={() => setFilter('done')}>
          Finished<b>{done.length}</b>
        </button>
        <div className="qtools__right">
          <Link to="/new" className="btn btn--primary">
            <span className="material-symbols-outlined" aria-hidden="true">add</span>
            New request
          </Link>
        </div>
      </div>

      {tickets === null ? (
        <p className="muted" style={{ marginTop: 16 }}>Loading…</p>
      ) : shown.length === 0 ? (
        <div className="empty" style={{ marginTop: 16 }}>
          <p className="empty__title">{filter === 'open' ? 'Nothing open' : 'Nothing finished yet'}</p>
          <p className="empty__body">
            {filter === 'open'
              ? 'When you raise something it shows up here, with its status and the whole conversation.'
              : 'Resolved and closed tickets collect here so you can find what was done last time.'}
          </p>
          {filter === 'open' ? (
            <Link to="/new" className="btn btn--primary" style={{ marginTop: 14 }}>
              Raise a request
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="card card--tight" style={{ marginTop: 16 }}>
          {shown.map((t) => {
            const type = typeById(t.requestTypeId);
            const replies = t.messages.filter((m) => !m.isInternal).length;
            return (
              <Link key={t.number} to={`/ticket/${t.number}`} className="tk">
                <span className="tk__icon">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    {type?.icon || 'confirmation_number'}
                  </span>
                </span>
                <span className="tk__main">
                  <b>{titleFor(t)}</b>
                  <small>
                    <span className="ref">{t.number}</span> <SourceChip ticket={t} /> <NewReplyChip ticket={t} unread={unread} /> · raised {shortWhen(t.raisedAt)}
                    {replies ? ` · ${replies} ${replies === 1 ? 'message' : 'messages'}` : ''}
                  </small>
                </span>
                <span className="tk__end">
                  <StatusChip ticket={t} />
                  <small>
                    <Pri priority={t.priority} ruleName={t.priorityRuleName} />
                  </small>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
